// Helpers for the Allocation Report "SITA Messages" panel.
// Messages are fetched on demand from /bookings/sita/messages, whose date range filters on the
// RECEIVED timestamp (not the flight date) and whose flight search is a substring match —
// so we query a window around the flight and keep exact flight + departure-date matches here.

export const SITA_MESSAGE_TYPES = ['PAL', 'CAL', 'PSM'];

// PALs can land a couple of days before departure; CALs keep coming up to (and just after) it
const DAYS_BEFORE = 3;
const DAYS_AFTER = 1;

const shiftDate = (yyyyMmDd, days) => {
    const d = new Date(`${yyyyMmDd}T00:00:00.000Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().split('T')[0];
};

// "FR0117" / "fr117" -> "FR117", so a zero-padded number in the message still matches the booking
export const normaliseFlightCode = (code) => {
    const m = String(code ?? '').trim().toUpperCase().match(/^([A-Z0-9]{2}[A-Z]?)0*(\d+[A-Z]?)$/);
    return m ? `${m[1]}${m[2]}` : String(code ?? '').trim().toUpperCase();
};

// The watcher stores most messages as SITA_RAW_MESSAGE: its type regex expects whitespace before
// the type line, but the raw text has the STX control char (\u0002) there. Read it from the content.
export const getSitaMessageType = (doc) => {
    if (SITA_MESSAGE_TYPES.includes(doc.type)) return doc.type;
    const m = String(doc.content ?? '').match(/^[\s\u0000-\u001f]*(PAL|CAL|PSM)\b/m);
    return m ? m[1] : 'Other';
};

// strip SOH/STX/ETX framing and normalise line endings for display
export const cleanSitaContent = (content) =>
    String(content ?? '')
        .replace(/\r\n?/g, '\n')
        .replace(/[\u0000-\u0008\u000b-\u001f]/g, '')
        .trim();

export const buildSitaMessagesQuery = ({ iata, flightCode, flightDate, bookingType }) => ({
    iata,
    start: shiftDate(flightDate, -DAYS_BEFORE),
    end: shiftDate(flightDate, DAYS_AFTER),
    limit: 50,
    skip: 0,
    type: bookingType === 'outbound' ? 'departure' : 'arrival',
    searchValue: flightCode,
    airport: '',
});

// exact flight + departure date, oldest first
export const selectFlightSitaMessages = (rows, { flightCode, flightDate }) => {
    const code = normaliseFlightCode(flightCode);
    return (Array.isArray(rows) ? rows : [])
        .filter((doc) =>
            normaliseFlightCode(doc.flightDetails?.flightCode) === code &&
            doc.flightDetails?.departureDate === flightDate
        )
        .sort((a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0));
};
